import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { SectionHeader } from "./SectionHeader";
import { NumberInput } from "./NumberInput";
import { FactorControls } from "./FactorControls";
import type { HeroRankTier } from "./types";

interface HeroRankScoringCardProps {
  heroRankWeight: number;
  onHeroRankWeightChange: (v: number) => void;
  heroRankTiers: HeroRankTier[];
  onTierPointsChange: (idx: number, v: number) => void;
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
}

export function HeroRankScoringCard({
  heroRankWeight,
  onHeroRankWeightChange,
  heroRankTiers,
  onTierPointsChange,
  enabled,
  onEnabledChange,
}: HeroRankScoringCardProps) {
  return (
    <Card id="kps-module-hero-rank" className={cn(!enabled && "bg-muted/30 order-1")}>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <SectionHeader
            title="Hero Rank Scoring"
            description="Keywords ranked but not yet in the top 10 earn the most points. That's where opportunity lives."
          />
          <FactorControls
            id="kps-toggle-hero-rank"
            enabled={enabled}
            onEnabledChange={onEnabledChange}
            value={heroRankWeight}
            onValueChange={onHeroRankWeightChange}
          />
        </div>

        <div className={cn("space-y-4", !enabled && "opacity-50 pointer-events-none select-none")}>
          <div className="space-y-0">
            <div className="grid grid-cols-[180px_1fr_100px] gap-4 items-center px-1 h-9 border-b border-border">
              <span className="text-xs font-semibold text-foreground">Condition</span>
              <span className="text-xs font-semibold text-foreground text-center">Rank Range</span>
              <span className="text-xs font-semibold text-foreground text-right">Points</span>
            </div>
            {heroRankTiers.map((row, idx) => (
              <div
                key={row.condition}
                className={cn(
                  "grid grid-cols-[180px_1fr_100px] gap-4 items-center px-1 py-3 border-b border-border last:border-b-0",
                  row.sweetSpot && "bg-status-success-soft -mx-1 px-2"
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-foreground">{row.condition}</span>
                  {row.sweetSpot && (
                    <Badge className="bg-[hsl(155_45%_75%)] text-foreground hover:bg-[hsl(155_45%_75%)]">
                      Sweet spot
                    </Badge>
                  )}
                </div>
                <span
                  className={cn(
                    "text-sm tabular-nums text-center",
                    row.nullRange ? "text-muted-foreground italic" : "text-foreground"
                  )}
                >
                  {row.range}
                </span>
                <div className="flex justify-end">
                  <NumberInput
                    value={row.points}
                    min={0}
                    max={heroRankWeight}
                    onValueChange={(v) => onTierPointsChange(idx, v)}
                    className="w-20 h-8 text-sm text-center rounded-md bg-card tabular-nums"
                  />
                </div>
              </div>
            ))}
          </div>

          <Alert variant="info">
            <Info className="h-4 w-4" />
            <AlertDescription>
              Rank boundaries (#1–#10, #11–#30, etc.) are fixed in the app. Only the points per tier are configurable here. Ask engineering to make boundaries editable if needed.
            </AlertDescription>
          </Alert>
        </div>
      </CardContent>
    </Card>
  );
}
